import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter
} from '@angular/core';
import { LocaleService } from '@app/core/i18n';
import { CatalogFilter } from '@app/shared/model/interfaces/catalog';
import { PlatformService } from '@app/shared/services/platform/platform.service';
import { ContentsRouteParameter, CatalogPage, CatalogFacetLink } from '@vestiaire/api-sdk-angular';
import { checkRouteParameters, FiltersService } from '@app/shared/components/catalog/services/filters.service';
import { IBaseItem } from '@app/shared/components/catalog/catalog-widgets/catalog-widget';
import { MappingServiceHash } from '../services/mapping.service';

interface FacetItems {
  [key: string]: number;
}

interface FacetList {
  [key: string]: FacetItems;
}

@Component({
  selector: 'vc-catalog-filters',
  templateUrl: './catalog-filters.component.html',
  styleUrls: ['./catalog-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogFiltersComponent implements OnInit, OnChanges {
  @Input()
  nbHits: number;
  @Input()
  facets: FacetList;
  @Input()
  filters: CatalogFilter[];
  @Input()
  refinedFacets: string[];
  @Input()
  page: CatalogPage | null;
  @Input()
  applyPriceFilter: Event;

  @Input()
  facetLink: CatalogFacetLink | null;
  routeParameters: ContentsRouteParameter[];

  @Input()
  showRefinementBody: boolean;

  @Input()
  currentCountry: string;

  @Input()
  userDSeligibility: boolean;

  @Output()
  clickedFilter = new EventEmitter<any>();

  @Input()
  catalogContainerOffset: number;

  /**
   * Minimum items for displaying a facet filter.
   */
  private minItems = 2;

  /**
   * Minimum threshold of hits (number of products)
   * for displaying a facet filter.
   *
   * Expressed in percent of total hits.
   */
  private hitsThreshold = 1;
  positionHeader = 0;

  trackByFilter = (index: number) => index;

  constructor(
    private localeService: LocaleService,
    private platformService: PlatformService,
    private mappingServiceHash: MappingServiceHash,
    private filtersService: FiltersService
  ) {}

  ngOnInit(): void {
    if (typeof this.filters === 'undefined') {
      this.filters = this.filtersService.getDefaultFilters(this.localeService);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.facets &&
      changes.facets.currentValue &&
      changes.facets.isFirstChange &&
      this.platformService.isServer
    ) {
      this.mappingServiceHash.init(changes.facets.currentValue);
    }
    if (changes.page && changes.page.currentValue) {
      this.routeParameters = changes.page.currentValue.route.parameters;
    }
  }

  ofType(filter, type): boolean {
    return filter.type === type;
  }

  /**
   * Check if a filter should be hidden.
   *
   * A filter is hidden if its number of items or associated hits
   * don't exceed configured thresholds.
   *
   * @see minItems
   * @see hitsThreshold
   */
  isHiddenFilter(filterOptions: CatalogFilter): boolean {
    let facet: string;

    let isHierarchicalFilter = false;
    if (typeof filterOptions.facet === 'string') {
      facet = filterOptions.facet; // Single-facet Widgets
    } else if (Array.isArray(filterOptions.facet) && filterOptions.facet.length > 0) {
      facet = filterOptions.facet[0]; // Hierarchical Menus
      isHierarchicalFilter = true;
    }

    const items = this.getFacetItems(facet);
    const facetHits = this.getFacetHits(facet);
    const hitsPercentage = this.nbHits && this.nbHits > 0 ? (facetHits / this.nbHits) * 100 : 0;

    let nbItems = items ? Object.keys(items).length : 0;

    if (nbItems > 0 && filterOptions.routeParameters && this.routeParameters) {
      Object.keys(items).forEach(itemKey => {
        const item = { value: itemKey } as IBaseItem;
        if (!checkRouteParameters(filterOptions.routeParameters, this.routeParameters, item)) {
          // Allow showing of depending facet if parent is hidden
          this.refinedFacets.push(facet);

          nbItems--;
        }
      });
    }
    const facetDependency = this.checkFacetDependency(filterOptions);

    if (!filterOptions.forceHide && !facetDependency) {
      /**
       * Show only if filter has items
       */

      return nbItems === 0;
    }

    return (
      (!isHierarchicalFilter && nbItems < this.minItems) ||
      hitsPercentage < this.hitsThreshold ||
      facetDependency ||
      nbItems === 0
    );
  }

  getFacets(): FacetList {
    return this.facets || {};
  }

  hasFacet(facet: string): boolean {
    return facet in this.getFacets();
  }

  getFacetItems(facet: string): FacetItems {
    let items;

    if (this.hasFacet(facet)) {
      items = this.getFacets()[facet];
    }
    return items || {};
  }

  getFacetHits(facet: string): number {
    let nbHits = 0;
    const items = this.getFacetItems(facet);

    for (const item of Object.keys(items)) {
      nbHits += items[item];
    }
    return nbHits;
  }

  scrollTop() {
    if (this.platformService.isBrowser) {
      window.scrollTo(0, this.catalogContainerOffset - 132);
    }
  }

  /**
   *  Check if facet depends on others facets (true => filter hidden)
   * @param filterOptions
   */
  checkFacetDependency(filterOptions: CatalogFilter): boolean {
    if (!filterOptions.requiredFacet) {
      return false;
    }

    if (!this.refinedFacets) {
      return true;
    }
    const requiredFacets = Array.isArray(filterOptions.requiredFacet)
      ? filterOptions.requiredFacet
      : [filterOptions.requiredFacet];

    let nbFacet = 0;
    requiredFacets.map(requiredFacet => {
      nbFacet += this.refinedFacets.indexOf(requiredFacet) !== -1 ? 1 : 0;
    });

    return nbFacet !== requiredFacets.length;
  }

  clickFilter(event) {
    this.clickedFilter.emit(event);
  }
}
